import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { requestWallet, selectUserCards } from 'common/features/store/duck/wallet/duck';
import { useRootSelector } from 'common/features/featuresReducer';
import { Card } from 'common/api/e-comm/models/Card';

import WalletCard from '../components/WalletCard';
import { WalletSortAndFilter } from './sortAndFilter';

import { delay } from 'common/utils';

import Skeleton from 'react-loading-skeleton';
import { useScrollRestore } from 'hooks/useScrollRestorer';
import {
    selectWalletAdBrandIds,
    selectWalletAdByBrandId,
    selectDealByBrandId,
} from 'common/features/store/duck/wallet-ads/duck';
import { useRequestWalletAdsIfNeeded } from 'common/hooks/useRequestWalletAdsIfNeeded';
import { useWalletAdDisplayLogic } from 'common/hooks/useWalletAdDisplayLogic';

const EmptyResultsMessage = ({ hasCards }: { hasCards: boolean }) =>
    !hasCards ? (
        <div className="text-lg py-36 text-brand text-center sm:text-2xl">
            No matching results. <br />
            Select another filter to view your cards
        </div>
    ) : (
        <div>
            <div className="h-96">&nbsp;</div>
            <div className="h-96">&nbsp;</div>
        </div>
    );

export const CardsView = ({ isActive }: { isActive: boolean }) => {
    const dispatch = useDispatch<any>();
    const { userId } = useRootSelector((s) => s.store.account);
    const myCardsState = useRootSelector(selectUserCards);
    const walletAdBrandIds = useRootSelector(selectWalletAdBrandIds);
    const walletAdSelector = useRootSelector(selectWalletAdByBrandId);
    const dealSelector = useRootSelector(selectDealByBrandId);
    const [hasFetchedCards, setHasFetchedCards] = useState<boolean>(
        Boolean(Object.keys(myCardsState).length)
    );
    const [cardsToRender, setCardsToRender] = useState<Card[]>([]);
    const requestWalletAdsIfNeeded = useRequestWalletAdsIfNeeded();

    const cardIdsThatShouldShowAds = useWalletAdDisplayLogic({
        sortedCards: cardsToRender,
        walletAdBrandIds,
        walletAdSelector,
        dealSelector,
        isActive: true,
    });

    useScrollRestore({ restoreOnMount: true, delayMs: 1000 });

    useEffect(() => {
        if (Object.keys(myCardsState).length > 0) {
            setHasFetchedCards(true);
        }
    }, [myCardsState]);

    // Fetch wallet ads from Contentful
    // Note: Ads are non-critical - errors are handled in the hook, but we're defensive here too
    useEffect(() => {
        if (isActive) {
            try {
                requestWalletAdsIfNeeded();
            } catch (error) {
                // Wallet ads are non-critical - ensure wallet continues to function
                console.error('Failed to request wallet ads in CardsView:', error);
            }
        }
    }, [isActive, requestWalletAdsIfNeeded]);

    useEffect(() => {
        // Update cards view when this tab is active -- useful when user happens to
        // `unarchive` a card.
        if (userId && isActive) {
            const prom = new Promise((resolve) => resolve(dispatch(requestWallet(userId))));

            prom.finally(async () => {
                await delay(1000);
                setHasFetchedCards(true);
            });
        }
    }, [dispatch, userId, isActive]);

    // don't render anything and avoid any work from WalletSortAndFilter
    if (!isActive) {
        return null;
    }

    if (!hasFetchedCards) {
        return (
            <div className="w-4/5 m-auto my-10">
                <Skeleton className="h-20" />
                <Skeleton className="mt-10 h-60" />
            </div>
        );
    }

    return (
        <>
            <WalletSortAndFilter
                allCards={Object.values(myCardsState) as Card[]}
                setViewableCards={setCardsToRender}
                result={cardsToRender.length || 0}
                shouldResetFilters={isActive}
                shouldPersistFilters={true}
            />
            {cardsToRender.length > 0 ? (
                cardsToRender.map((card, index) => (
                    <WalletCard
                        idx={index}
                        key={card.id}
                        card={card}
                        archived={false}
                        shouldShowAd={cardIdsThatShouldShowAds.has(card.id)}
                    />
                ))
            ) : (
                <EmptyResultsMessage hasCards={Object.keys(myCardsState).length > 0} />
            )}
        </>
    );
};
